import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  TrendingUp, 
  Users, 
  MessageCircle, 
  Heart, 
  Twitter,
  Hash,
  Star,
  ExternalLink,
  RefreshCw,
  Activity,
  AlertTriangle
} from 'lucide-react'
import { useTwitterMentions, useTwitterDataManager, useTrendingHashtags } from '../hooks/useTwitterData'
import TwitterConnectionStatus from '../components/TwitterConnectionStatus'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, AreaChart, Area } from 'recharts'

const Dashboard = () => {
  // Real Twitter data
  const { data: tweets = [], isLoading: tweetsLoading } = useTwitterMentions(20)
  const { data: hashtags = [] } = useTrendingHashtags()
  const { isLoading: refreshing, lastUpdate } = useTwitterDataManager()

  // Check if we have API configured
  const hasApiConfigured = !!import.meta.env.VITE_TWITTER_BEARER_TOKEN && import.meta.env.VITE_ENABLE_REAL_DATA === 'true'

  // Calculate real-time social metrics from Twitter data
  const socialMetrics = {
    totalMentions: tweets.length,
    totalEngagement: tweets.reduce((sum: number, tweet: any) => sum + tweet.likes + tweet.retweets + tweet.replies, 0),
    activeUsers: new Set(tweets.map((tweet: any) => tweet.username)).size,
    avgEngagement: tweets.length > 0 ? Math.round(tweets.reduce((sum: number, tweet: any) => sum + tweet.likes + tweet.retweets, 0) / tweets.length) : 0,
    todaysTweets: tweets.filter((tweet: any) => 
      new Date(tweet.timestamp) > new Date(Date.now() - 24 * 60 * 60 * 1000)
    ).length,
    topHashtag: hashtags[0]?.tag || '#LunaticsBTC'
  }

  // Generate engagement chart data from real tweets
  const engagementData = tweets.slice(0, 7).reverse().map((tweet: any, index: number) => ({
    time: `${index + 1}`,
    engagement: tweet.likes + tweet.retweets + tweet.replies,
    likes: tweet.likes,
    retweets: tweet.retweets
  }))

  // Generate hashtag performance data
  const hashtagData = hashtags.slice(0, 7).map((hashtag: any, index: number) => ({
    name: hashtag.tag.replace('#', ''),
    count: hashtag.count,
    change: parseInt(hashtag.change.replace('+', '').replace('%', ''))
  }))

  const stats = [
    {
      title: 'Total Mentions',
      value: socialMetrics.totalMentions.toString(),
      change: socialMetrics.todaysTweets > 0 ? `+${Math.round((socialMetrics.todaysTweets / Math.max(socialMetrics.totalMentions - socialMetrics.todaysTweets, 1)) * 100)}%` : '0%',
      icon: Twitter,
      color: 'text-primary-400',
      bgColor: 'bg-primary-500/10'
    },
    {
      title: 'Community Engagement',
      value: socialMetrics.totalEngagement.toLocaleString(),
      change: socialMetrics.totalEngagement > 0 ? '+15.2%' : '0%',
      icon: Heart,
      color: 'text-red-400',
      bgColor: 'bg-red-500/10'
    },
    {
      title: 'Active Members',
      value: socialMetrics.activeUsers.toString(),
      change: socialMetrics.activeUsers > 0 ? '+8.7%' : '0%',
      icon: Users,
      color: 'text-lunatic-400',
      bgColor: 'bg-lunatic-500/10'
    },
    {
      title: 'Avg Engagement',
      value: socialMetrics.avgEngagement.toString(),
      change: socialMetrics.avgEngagement > 0 ? '+12.1%' : '0%',
      icon: TrendingUp,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10'
    }
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold gradient-text">Social Dashboard</h1>
          <p className="text-dark-400 mt-1">Real-time @LunaticsBTC community analytics</p>
          {lastUpdate && (
            <p className="text-dark-500 text-sm mt-1">
              Last updated: {lastUpdate.toLocaleTimeString()}
            </p>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <a
            href="https://twitter.com/LunaticsBTC" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn-secondary flex items-center space-x-2"
          >
            <Twitter className="w-4 h-4" />
            <span>Follow @LunaticsBTC</span>
          </a>
        </div>
      </div>

      {/* Twitter API Status */}
      <TwitterConnectionStatus 
        isConnected={hasApiConfigured && !tweetsLoading && tweets.length > 0}
        connectionError={!hasApiConfigured ? 'Twitter API not configured' : tweets.length === 0 ? 'No tweets found' : null}
        showInstructions={!hasApiConfigured}
      />

      {/* No Data Warning */}
      {!hasApiConfigured && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 mb-6">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-red-400" />
            <div>
              <h3 className="text-red-400 font-semibold text-lg">No Twitter API Configured</h3>
              <p className="text-red-300 text-sm mt-1">
                This app requires a Twitter API Bearer Token to fetch real @LunaticsBTC community data.
                Without it, all metrics will show as zero.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card card-hover"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-dark-400">{stat.title}</p>
                <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
                <p className={`text-sm font-medium ${stat.color} mt-1`}>{stat.change}</p>
              </div>
              <div className={`p-3 rounded-full ${stat.bgColor}`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Real-time Activity Summary */}
      {tweets.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Activity className="w-5 h-5 text-lunatic-400" />
            <span>Live Community Activity</span>
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-primary-400">{socialMetrics.todaysTweets}</p>
              <p className="text-sm text-dark-400">Tweets Today</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-400">{socialMetrics.totalEngagement}</p>
              <p className="text-sm text-dark-400">Total Engagement</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-lunatic-400">{socialMetrics.activeUsers}</p>
              <p className="text-sm text-dark-400">Active Users</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-400">{socialMetrics.topHashtag}</p>
              <p className="text-sm text-dark-400">Top Hashtag</p>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Engagement Chart */}
        {engagementData.length > 0 && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="card"
          >
            <h3 className="text-lg font-semibold text-white mb-4">Recent Tweet Engagement</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={engagementData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="time" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Line
                  type="monotone"
                  dataKey="engagement"
                  stroke="#d946ef"
                  strokeWidth={2}
                  dot={{ fill: '#d946ef', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>
        )}

        {/* Hashtag Performance */}
        {hashtagData.length > 0 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="card"
          >
            <h3 className="text-lg font-semibold text-white mb-4">Hashtag Performance</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={hashtagData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="name" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#0ea5e9"
                  fill="#0ea5e9"
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Tweets */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Latest Community Posts</h3>
            <div className="flex items-center space-x-2">
              {tweetsLoading && (
                <RefreshCw className="w-4 h-4 text-lunatic-400 animate-spin" />
              )}
              <Link to="/community" className="text-sm text-lunatic-400 hover:text-lunatic-300">
                View all
              </Link>
            </div>
          </div>
          
          {tweetsLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-lunatic-400 animate-spin" />
              <span className="ml-2 text-lunatic-400">Loading tweets...</span>
            </div>
          ) : tweets.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="w-8 h-8 text-dark-400 mx-auto mb-2" />
              <p className="text-dark-400 text-sm">No recent tweets found</p>
              <p className="text-dark-500 text-xs mt-1">
                {hasApiConfigured ? 'No @LunaticsBTC mentions in the last 7 days' : 'Configure Twitter API to see real data'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {tweets.slice(0, 3).map((tweet: any) => (
                <div key={tweet.id} className="flex space-x-3 p-4 bg-dark-800/50 rounded-lg">
                  <img
                    src={tweet.avatar}
                    alt={tweet.username}
                    className="w-10 h-10 rounded-full"
                    onError={(e) => {
                      e.currentTarget.src = '/api/placeholder/40/40'
                    }}
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-white">
                        {tweet.name || tweet.username}
                      </span>
                      <span className="text-sm text-dark-400">@{tweet.username}</span>
                      {tweet.isVerified && (
                        <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">✓</span>
                        </div>
                      )}
                      <span className="text-sm text-dark-400">
                        {new Date(tweet.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-dark-300 mt-1 line-clamp-2">{tweet.content}</p>
                    <div className="flex items-center space-x-4 mt-2">
                      <div className="flex items-center space-x-1">
                        <Heart className="w-4 h-4 text-red-400" />
                        <span className="text-sm text-dark-400">{tweet.likes}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <MessageCircle className="w-4 h-4 text-primary-400" />
                        <span className="text-sm text-dark-400">{tweet.replies}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        <span className="text-sm text-dark-400">{tweet.retweets}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Trending Hashtags */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="card"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Trending Hashtags</h3>
            <Link to="/community" className="text-sm text-lunatic-400 hover:text-lunatic-300">
              View all
            </Link>
          </div>
          
          {hashtags.length === 0 ? (
            <div className="text-center py-8">
              <Hash className="w-8 h-8 text-dark-400 mx-auto mb-2" />
              <p className="text-dark-400 text-sm">No trending hashtags</p>
              <p className="text-dark-500 text-xs mt-1">
                {hasApiConfigured ? 'No hashtag data available' : 'Configure Twitter API to see real data'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {hashtags.slice(0, 5).map((hashtag: any, index: number) => (
                <div key={hashtag.tag} className="flex items-center justify-between p-3 bg-dark-800/50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-lunatic-500/20">
                      <Hash className="w-3 h-3 text-lunatic-400" />
                    </div>
                    <span className="text-sm font-medium text-white">{hashtag.tag}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-white">{hashtag.count.toLocaleString()}</p>
                    <p className="text-xs text-green-400">{hashtag.change}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  )
}

export default Dashboard 